import React, { useState, useMemo, useEffect } from 'react'
import Modal, { ModalHeader, ModalFooter } from '@app/components/Modal'
import * as S from '@app/components/Modal/styled'
import * as ListS from './styled'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import { GeneratedForms } from '../../../types/Templates'
import { useSnackbar } from '@app/snackbar'
import { fetch } from '../../../../common/fetch'
import getEnv from '@app/environments/getEnv'
import theme from '@app/ui/theme'
import { downloadFolder } from './FinalRatingItem'
import DateTimeView from '../../atoms/DateTimeView'
import { Eval } from '@app/evaluation-core/types'
import {
  FaArchive,
  FaCheckCircle,
  FaEnvelope,
  FaTrashRestore,
  FaEye,
  FaEyeSlash,
  FaCircle,
} from 'react-icons/fa'
import {
  DropDownMenu,
  DropDownMenuContext,
} from '@app/ui/dropdown-menu/DropDownMenu'
import { DropDownMenuItem } from '@app/ui/dropdown-menu/DropDownMenu.styles'
import { FormExGenderBtn } from '@app/evaluation-settings-menu/HeaderArea.styles'
import CustomSlopeTablesModal from './CustomSlopeTablesModal'
import { formatManualVersion } from '@common/utils/formatManualVersion'
import { InLineSpinner } from '@app/evaluation/components/Spinner'

type ActionButtonProps = {
  label: string
  onClick: () => void
  disabled?: boolean
  bgColor?: string
  title: string
}

const ActionButton: React.FC<ActionButtonProps> = ({
  label,
  onClick,
  disabled,
  bgColor,
  title,
}) => (
  <div title={title} style={{ display: 'inline-flex' }}>
    <RoundedButton
      title={label}
      onPress={onClick}
      disabled={disabled}
      bgColor={bgColor}
    />
  </div>
)

type FormMenuItemProps = {
  label: string
  icon: React.ReactNode
  onPress: () => void
  disabled?: boolean
}

// Closes the menu before acting, so a row never keeps an open menu over the
// state it just changed.
const FormMenuItem: React.FC<FormMenuItemProps> = ({
  label,
  icon,
  onPress,
  disabled,
}) => {
  const { close } = React.useContext(DropDownMenuContext)

  return (
    <DropDownMenuItem
      disabled={disabled}
      onPress={() => {
        close()
        onPress()
      }}
    >
      <FormExGenderBtn>
        <span>{label}</span>
        <div>{icon}</div>
      </FormExGenderBtn>
    </DropDownMenuItem>
  )
}

const HeaderDetail: React.FC<{
  label: string
  value: React.ReactNode
  weight: number
  minWidth?: number
}> = ({ label, value, weight, minWidth }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      gap: '2px',
      flex: `${weight} 1 0`,
      minWidth: minWidth ? `${minWidth}px` : undefined,
    }}
  >
    <span
      style={{
        fontSize: '13px',
        letterSpacing: '1px',
        fontWeight: 400,
        color: 'rgba(0, 0, 0, 0.8)',
      }}
    >
      {label}
    </span>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        minHeight: '32px',
        fontSize: '16px',
        fontWeight: 600,
        color: 'rgba(0, 0, 0, 0.8)',
      }}
    >
      {value}
    </div>
  </div>
)

type ManageFormsModalProps = {
  isOpen: boolean
  onClose: () => void
  forms: GeneratedForms[]
  evaluation: Eval | undefined
  templateId: string | undefined
  clubName: string
  ratingName?: string
  finalizedAt?: string | null
  ratingDate?: string | null
  teamLeader?: string
  revision?: number | string | null
  holesCount?: number
  manualVersion?: string
  onRegenerateForms: () => Promise<void>
  onFormsUpdated?: () => Promise<void>
  isGeneratingForms?: boolean
}

const ManageFormsModal: React.FC<ManageFormsModalProps> = ({
  isOpen,
  onClose,
  forms,
  evaluation,
  templateId,
  clubName,
  ratingName,
  finalizedAt,
  ratingDate,
  teamLeader,
  revision,
  holesCount,
  manualVersion,
  onRegenerateForms,
  onFormsUpdated,
  isGeneratingForms,
}) => {
  const [showArchived, setShowArchived] = useState(false)
  const [isProcessing, setIsProcessing] = useState<string | null>(null)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [localForms, setLocalForms] = useState<GeneratedForms[]>(forms)
  const [emailFormId, setEmailFormId] = useState<string | null>(null)
  const [emailInput, setEmailInput] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [customSlopeFormId, setCustomSlopeFormId] = useState<string | null>(
    null
  )
  const [openSnackbar] = useSnackbar()

  const regenerating = isRegenerating || !!isGeneratingForms
  const hasGeneratedForms = localForms.length > 0

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Sync local forms when props change or modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalForms(forms)
    }
  }, [forms, isOpen])

  const sortedForms = useMemo(() => {
    return [...localForms].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
  }, [localForms])

  const filteredForms = useMemo(() => {
    if (showArchived) return sortedForms
    return sortedForms.filter((form) => !form.archived)
  }, [sortedForms, showArchived])

  const handleActivateForm = async (formId: string) => {
    if (!templateId) return

    // Update local state immediately for instant feedback
    setLocalForms((prev) =>
      prev.map((form) => ({
        ...form,
        activeForEvaluation: form.id === formId ? true : false,
      }))
    )

    setIsProcessing(formId)
    try {
      await fetch.post(
        `${
          getEnv().api
        }/ratings/templates/${templateId}/forms/${formId}/activate`
      )
      openSnackbar('Form activated successfully')
      await onFormsUpdated?.()
    } catch (error) {
      console.error('Error activating form:', error)
      openSnackbar('Failed to activate form')
      // Revert on error
      setLocalForms(forms)
    } finally {
      setIsProcessing(null)
    }
  }

  const handleDeactivateForm = async (formId: string) => {
    if (!templateId) return

    // Update local state immediately for instant feedback
    setLocalForms((prev) =>
      prev.map((form) => ({
        ...form,
        activeForEvaluation:
          form.id === formId ? false : form.activeForEvaluation,
      }))
    )

    setIsProcessing(formId)
    try {
      await fetch.post(
        `${
          getEnv().api
        }/ratings/templates/${templateId}/forms/${formId}/deactivate`
      )
      openSnackbar('Form deactivated successfully')
      await onFormsUpdated?.()
    } catch (error) {
      console.error('Error deactivating form:', error)
      openSnackbar('Failed to deactivate form')
      // Revert on error
      setLocalForms(forms)
    } finally {
      setIsProcessing(null)
    }
  }

  const handleArchiveForm = async (formId: string) => {
    if (!templateId) return

    // Update local state immediately for instant feedback
    setLocalForms((prev) =>
      prev.map((form) =>
        form.id === formId ? { ...form, archived: true } : form
      )
    )

    setIsProcessing(formId)
    try {
      await fetch.post(
        `${
          getEnv().api
        }/ratings/templates/${templateId}/forms/${formId}/archive`
      )
      openSnackbar('Form archived successfully')
      await onFormsUpdated?.()
    } catch (error) {
      console.error('Error archiving form:', error)
      openSnackbar('Failed to archive form')
      // Revert on error
      setLocalForms(forms)
    } finally {
      setIsProcessing(null)
    }
  }

  const handleUnarchiveForm = async (formId: string) => {
    if (!templateId) return

    // Update local state immediately for instant feedback
    setLocalForms((prev) =>
      prev.map((form) =>
        form.id === formId ? { ...form, archived: false } : form
      )
    )

    setIsProcessing(formId)
    try {
      await fetch.post(
        `${
          getEnv().api
        }/ratings/templates/${templateId}/forms/${formId}/unarchive`
      )
      openSnackbar('Form unarchived successfully')
      await onFormsUpdated?.()
    } catch (error) {
      console.error('Error unarchiving form:', error)
      openSnackbar('Failed to unarchive form')
      // Revert on error
      setLocalForms(forms)
    } finally {
      setIsProcessing(null)
    }
  }

  const handleDownloadForm = async (form: GeneratedForms) => {
    try {
      await downloadFolder(form.zipPath)
    } catch (error) {
      console.error('Error downloading form:', error)
      openSnackbar('Failed to download form')
    }
  }

  const handleEmailFormClick = (formId: string) => {
    setEmailFormId(formId)
    setEmailInput('')
    setEmailError(null)
  }

  const handleCancelEmail = () => {
    setEmailFormId(null)
    setEmailInput('')
    setEmailError(null)
  }

  const handleSendEmail = async (form: GeneratedForms) => {
    if (!templateId) return

    if (!emailInput.trim()) {
      setEmailError('Email is required')
      return
    }

    if (!validateEmail(emailInput)) {
      setEmailError('Please enter a valid email address')
      return
    }

    const now = new Date()
    // Update local state immediately for instant feedback
    setLocalForms((prev) =>
      prev.map((f) => (f.id === form.id ? { ...f, lastEmailedAt: now } : f))
    )

    try {
      await fetch.post(`${getEnv().api}/ratings/forms/email-link`, {
        email: emailInput.trim(),
        clubName: clubName,
        courseName: form.courseName || evaluation?.courseName || '',
        ratingName: form.name,
        templateId: templateId,
        formId: form.id,
      })
      openSnackbar('Download link sent successfully')
      await onFormsUpdated?.()
      setEmailFormId(null)
      setEmailInput('')
    } catch (error) {
      console.error('Error sending email:', error)
      openSnackbar('Failed to send email link')
      // Revert on error
      setLocalForms(forms)
    }
  }

  const handleRegenerateForms = async () => {
    const regenerated = hasGeneratedForms
    setIsRegenerating(true)
    try {
      await onRegenerateForms()
      await onFormsUpdated?.()
      openSnackbar(
        regenerated
          ? 'Forms regenerated successfully'
          : 'Forms generated successfully'
      )
    } catch (error) {
      console.error('Error regenerating forms:', error)
      openSnackbar(
        regenerated ? 'Failed to regenerate forms' : 'Failed to generate forms'
      )
    } finally {
      setIsRegenerating(false)
    }
  }

  const handleClose = () => {
    onClose()
    setShowArchived(false)
    setLocalForms(forms)
  }

  const customSlopeForm = customSlopeFormId
    ? localForms.find((f) => f.id === customSlopeFormId)
    : undefined

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        width="1100px"
        height="auto"
        closeOnBackdropClick={true}
        showCloseButton={true}
      >
        <ModalHeader>
          <h2
            style={{
              paddingTop: '8px',
              margin: 0,
              fontSize: '24px',
              fontWeight: 600,
            }}
          >
            Manage Forms
          </h2>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px 16px',
              marginTop: '12px',
              marginBottom: '8px',
            }}
          >
            <HeaderDetail
              label="Name"
              value={ratingName || '-'}
              weight={15}
              minWidth={100}
            />
            <HeaderDetail
              label="Finalized Date"
              value={
                finalizedAt ? <DateTimeView dateString={finalizedAt} /> : '-'
              }
              weight={10}
              minWidth={160}
            />
            <HeaderDetail
              label="Rating Date"
              value={
                ratingDate ? (
                  <DateTimeView dateString={ratingDate} showTime={false} />
                ) : (
                  '-'
                )
              }
              weight={10}
              minWidth={160}
            />
            <HeaderDetail
              label="Team Leader"
              value={teamLeader || '-'}
              weight={15}
            />
            <HeaderDetail
              label="Revision"
              value={
                <ListS.ClubHoleCount style={{ borderRadius: '8px', margin: 0 }}>
                  {revision ?? '-'}
                </ListS.ClubHoleCount>
              }
              weight={5}
              minWidth={50}
            />
            <HeaderDetail
              label="Holes"
              value={
                <ListS.LockedHolesCount style={{ margin: 0 }}>
                  {holesCount ?? '-'}
                </ListS.LockedHolesCount>
              }
              weight={5}
              minWidth={50}
            />
            <HeaderDetail
              label="Manual Version"
              value={formatManualVersion(manualVersion)}
              weight={5}
            />
          </div>
        </ModalHeader>
        <S.ModalScrollContent style={{ padding: '24px' }}>
          <ListS.TableContainer>
            <ListS.Table>
              <thead>
                <ListS.TableRowHead>
                  <ListS.TableHeadCol style={{ width: '8%', minWidth: '90px' }}>
                    Status
                  </ListS.TableHeadCol>
                  <ListS.TableHeadCol
                    style={{ width: '10%', minWidth: '160px' }}
                  >
                    Created
                  </ListS.TableHeadCol>
                  <ListS.TableHeadCol
                    style={{ width: '12%', minWidth: '140px' }}
                  >
                    Generated By
                  </ListS.TableHeadCol>
                  <ListS.TableHeadCol
                    style={{ width: '10%', minWidth: '160px' }}
                  >
                    Last Emailed
                  </ListS.TableHeadCol>
                  <ListS.TableHeadCol style={{ width: '30%' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        flexWrap: 'wrap',
                        gap: '12px',
                      }}
                    >
                      <span style={{ fontSize: '14px', color: '#666' }}>
                        {filteredForms.length} form
                        {filteredForms.length !== 1 ? 's' : ''} available
                      </span>
                      <button
                        onClick={() => setShowArchived(!showArchived)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          border: '1px solid #e4e4e4',
                          borderRadius: '6px',
                          backgroundColor: showArchived ? '#f0f7ff' : 'white',
                          cursor: 'pointer',
                          fontSize: '13px',
                          color: showArchived ? theme.colors.primary : '#666',
                        }}
                      >
                        {showArchived ? (
                          <FaEyeSlash size={14} />
                        ) : (
                          <FaEye size={14} />
                        )}
                        {showArchived ? 'Hide Archived' : 'Show Archived'}
                      </button>
                    </div>
                  </ListS.TableHeadCol>
                </ListS.TableRowHead>
              </thead>
              <tbody>
                {regenerating && (
                  <tr>
                    <ListS.GeneratingFormsCell colSpan={5}>
                      <ListS.GeneratingFormsContent>
                        <InLineSpinner size={22} />
                        <ListS.GeneratingFormsLabel>
                          Generating new forms...
                        </ListS.GeneratingFormsLabel>
                      </ListS.GeneratingFormsContent>
                    </ListS.GeneratingFormsCell>
                  </tr>
                )}
                {filteredForms.map((form) => (
                  <React.Fragment key={form.id}>
                    <ListS.TableRow
                      style={{
                        backgroundColor: form.archived ? '#fafafa' : 'white',
                        opacity: form.archived ? 0.7 : 1,
                      }}
                    >
                      <ListS.TableCol>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                          }}
                        >
                          {form.activeForEvaluation && (
                            <ListS.RowMarkerIcon>
                              <FaCheckCircle color={'green'} />
                            </ListS.RowMarkerIcon>
                          )}
                          {form.archived && (
                            <span
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '12px',
                                color: '#856404',
                                backgroundColor: '#fff3cd',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontWeight: 500,
                                width: 'fit-content',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <FaArchive size={12} />
                              Archived
                            </span>
                          )}
                        </div>
                      </ListS.TableCol>
                      <ListS.TableCol>
                        <DateTimeView
                          date={
                            form.createdAt
                              ? new Date(form.createdAt)
                              : new Date()
                          }
                        />
                      </ListS.TableCol>
                      <ListS.TableCol>
                        {form.generatedBy ? (
                          <span style={{ fontSize: '14px' }}>
                            {form.generatedBy}
                          </span>
                        ) : (
                          <span style={{ color: '#999', fontSize: '13px' }}>
                            -
                          </span>
                        )}
                      </ListS.TableCol>
                      <ListS.TableCol>
                        {form.lastEmailedAt ? (
                          <DateTimeView date={new Date(form.lastEmailedAt)} />
                        ) : (
                          <span style={{ color: '#999', fontSize: '13px' }}>
                            Never
                          </span>
                        )}
                      </ListS.TableCol>
                      <ListS.TableCol>
                        <ListS.FormActions>
                          <DropDownMenu
                            value={''}
                            isMenu={true}
                            width={250}
                            relative
                            xAdj={-150}
                            useShadow={false}
                          >
                            {form.activeForEvaluation ? (
                              <FormMenuItem
                                label="Deactivate form"
                                icon={<FaCircle size={16} color={'#adb5bd'} />}
                                onPress={() => handleDeactivateForm(form.id)}
                                disabled={
                                  form.archived || isProcessing === form.id
                                }
                              />
                            ) : (
                              <FormMenuItem
                                label="Set active"
                                icon={
                                  <FaCheckCircle size={20} color={'green'} />
                                }
                                onPress={() => handleActivateForm(form.id)}
                                disabled={
                                  form.archived || isProcessing === form.id
                                }
                              />
                            )}
                            {form.archived ? (
                              <FormMenuItem
                                label="Unarchive"
                                icon={
                                  <FaTrashRestore
                                    size={20}
                                    color={theme.colors.primary}
                                  />
                                }
                                onPress={() => handleUnarchiveForm(form.id)}
                                disabled={isProcessing === form.id}
                              />
                            ) : (
                              <FormMenuItem
                                label="Archive"
                                icon={
                                  <FaArchive
                                    size={20}
                                    color={theme.colors.primary}
                                  />
                                }
                                onPress={() => handleArchiveForm(form.id)}
                                disabled={isProcessing === form.id}
                              />
                            )}
                          </DropDownMenu>
                          <ActionButton
                            label="Email link"
                            onClick={() => handleEmailFormClick(form.id)}
                            disabled={
                              isProcessing === form.id ||
                              emailFormId === form.id
                            }
                            title="Email Download Link"
                          />
                          <ActionButton
                            label={'Slope\ntables'}
                            onClick={() => setCustomSlopeFormId(form.id)}
                            disabled={form.archived || isProcessing === form.id}
                            title="Custom Slope Tables"
                          />
                          <ListS.FormActionsDownload>
                            <ActionButton
                              label={'Download\nforms'}
                              onClick={() => handleDownloadForm(form)}
                              disabled={isProcessing === form.id}
                              bgColor={theme.colors.secondary2}
                              title="Download Forms"
                            />
                          </ListS.FormActionsDownload>
                        </ListS.FormActions>
                      </ListS.TableCol>
                    </ListS.TableRow>
                    {emailFormId === form.id && (
                      <tr
                        style={{
                          backgroundColor: '#f8f9fa',
                        }}
                      >
                        <td colSpan={5} style={{ padding: '0' }}>
                          <div
                            style={{
                              padding: '16px 20px',
                              borderBottom: '1px solid #e4e4e4',
                              backgroundColor: '#f0f7ff',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                flexWrap: 'wrap',
                              }}
                            >
                              <span
                                style={{
                                  fontSize: '14px',
                                  fontWeight: 500,
                                  color: '#333',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Send download link to:
                              </span>
                              <div
                                style={{
                                  display: 'flex',
                                  flex: 1,
                                  gap: '8px',
                                  minWidth: '300px',
                                }}
                              >
                                <input
                                  type="email"
                                  value={emailInput}
                                  onChange={(e) => {
                                    setEmailInput(e.target.value)
                                    if (emailError) setEmailError(null)
                                  }}
                                  placeholder="recipient@example.com"
                                  autoFocus
                                  style={{
                                    flex: 1,
                                    padding: '10px 14px',
                                    border: `1px solid ${
                                      emailError ? '#dc3545' : '#ced4da'
                                    }`,
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    outline: 'none',
                                  }}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      handleSendEmail(form)
                                    }
                                  }}
                                />
                                <button
                                  onClick={() => handleSendEmail(form)}
                                  disabled={!emailInput.trim()}
                                  style={{
                                    padding: '10px 20px',
                                    backgroundColor: emailInput.trim()
                                      ? '#28a745'
                                      : '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: emailInput.trim()
                                      ? 'pointer'
                                      : 'not-allowed',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                  }}
                                >
                                  <FaEnvelope size={14} />
                                  Send
                                </button>
                                <button
                                  onClick={handleCancelEmail}
                                  style={{
                                    padding: '10px 16px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                            {emailError && (
                              <div
                                style={{
                                  marginTop: '8px',
                                  color: '#dc3545',
                                  fontSize: '13px',
                                  marginLeft: '130px',
                                }}
                              >
                                {emailError}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </ListS.Table>
            {filteredForms.length === 0 && !regenerating && (
              <div
                style={{
                  padding: '40px',
                  textAlign: 'center',
                  color: '#666',
                }}
              >
                <p style={{ margin: 0, fontSize: '14px' }}>
                  {!hasGeneratedForms
                    ? 'No forms have been generated yet.'
                    : showArchived
                    ? 'No archived forms found'
                    : 'No active forms available. Toggle "Show Archived" to see archived forms.'}
                </p>
              </div>
            )}
          </ListS.TableContainer>
        </S.ModalScrollContent>
        <ModalFooter>
          <RoundedButton
            title={
              regenerating
                ? hasGeneratedForms
                  ? 'Regenerating...'
                  : 'Generating...'
                : hasGeneratedForms
                ? 'Regenerate Forms'
                : 'Generate Forms'
            }
            onPress={handleRegenerateForms}
            disabled={regenerating}
            bgColor={theme.colors.secondary2}
          />
          <RoundedButton
            title="Close"
            onPress={handleClose}
            style={{
              backgroundColor: theme.colors.white,
              border: `1px solid ${theme.colors.secondary}`,
              color: theme.colors.secondary,
            }}
            textStyle={{
              color: theme.colors.secondary,
            }}
          />
        </ModalFooter>
      </Modal>
      <CustomSlopeTablesModal
        isOpen={customSlopeFormId !== null}
        onClose={() => setCustomSlopeFormId(null)}
        evaluation={evaluation}
        templateId={templateId}
        form={customSlopeForm}
      />
    </>
  )
}

export default ManageFormsModal
